import {EventIdSchema} from '@cohort/shared/schema/apps';
import {DateSchema} from '@cohort/shared/schema/common';
import {DataTypeSchema} from '@cohort/shared/schema/common/rules/dataTypes';
import {
  CohortMembershipCriterionOperatorSchema,
  OperatorIdSchema,
  UserEventCriterionOperatorSchema,
} from '@cohort/shared/schema/common/rules/operators';
import {z} from 'zod';

const CriterionInputValueSchema = z
  .string()
  .min(1, {message: 'errorRequired'})
  .or(z.array(z.string()).min(1, {message: 'errorRequired'}))
  .or(DateSchema)
  .or(z.number())
  .or(z.boolean());

export const UserPropertyCriterionSchema = z.object({
  id: z.string().uuid().nullable(),
  userPropertyId: z.string().uuid().optional(),
  operator: OperatorIdSchema.optional(),
  value: CriterionInputValueSchema.optional(),
  valueType: DataTypeSchema.optional(),
  type: z.enum(['user-property']),
});
export const UserPropertyCriterionSchemaRequired = UserPropertyCriterionSchema.required();

export const UserEventCriterionFilterSchema = z.object({
  id: z.string().uuid().nullable(),
  propertyName: z.string().optional(),
  operator: OperatorIdSchema.optional(),
  value: CriterionInputValueSchema.optional(),
  valueType: DataTypeSchema.optional(),
});
export const UserEventCriterionFilterSchemaRequired = UserEventCriterionFilterSchema.required();

export const UserEventCriterionSchema = z.object({
  id: z.string().uuid().nullable(),
  userEventId: EventIdSchema.optional(),
  operator: UserEventCriterionOperatorSchema.optional(),
  count: z.number().optional(),
  filters: z.array(UserEventCriterionFilterSchema).optional(),
  type: z.enum(['user-event']),
});
export const UserEventCriterionSchemaRequired = UserEventCriterionSchema.extend({
  filters: z.array(UserEventCriterionFilterSchemaRequired),
}).required();

export const CohortMembershipCriterionSchema = z.object({
  id: z.string().uuid().nullable(),
  cohortId: z.string().uuid().optional(),
  operator: CohortMembershipCriterionOperatorSchema.optional(),
  type: z.enum(['cohort-membership']),
});
export const CohortMembershipCriterionSchemaRequired = CohortMembershipCriterionSchema.required();

export const NullCriterionSchema = z.object({
  id: z.string().uuid().nullable(),
  type: z.null(),
});

export const CriteronSchema = z.discriminatedUnion('type', [
  UserPropertyCriterionSchema,
  UserEventCriterionSchema,
  CohortMembershipCriterionSchema,
  NullCriterionSchema,
]);
export type CriteronType = z.infer<typeof CriteronSchema>;

// Zod has a deepPartial method but it doesn't provide a deepRequired method
const CriterionSchemaRequired = z.discriminatedUnion('type', [
  UserPropertyCriterionSchemaRequired,
  UserEventCriterionSchemaRequired,
  CohortMembershipCriterionSchemaRequired,
]);

export const RuleGroupSchema = z.object({
  id: z.string().uuid().nullable(),
  criteria: z.array(CriteronSchema),
});
export type RuleGroupType = z.infer<typeof RuleGroupSchema>;

const RuleGroupSchemaRequired = z.object({
  id: z.string().uuid().nullable(),
  criteria: z.array(CriterionSchemaRequired),
});

export const DynamicCohortRuleSchema = z.object({
  id: z.string().uuid().nullable(),
  groups: z.array(RuleGroupSchema),
});
export type DynamicCohortRuleType = z.infer<typeof DynamicCohortRuleSchema>;

export const DynamicCohortRuleSchemaRequired = z.object({
  id: z.string().uuid().nullable(),
  groups: z.array(RuleGroupSchemaRequired),
});
